/**
 * Common headers for API requests.
 */
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

/**
 * Standard error messages for API responses.
 */
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error occurred. Please check your connection.',
  SERVER_ERROR: 'Server error occurred. Please try again later.',
  NOT_FOUND: 'Resource not found.',
  SESSION_EXPIRED: 'Your session has expired. Please start a new conversation.',
  UNAUTHORIZED: 'Authentication required. Please sign in again.',
  VALIDATION_ERROR: 'Validation error occurred.',
  INVALID_CONTENT_TYPE: 'Invalid content type. Expected application/json.',
} as const;

/**
 * HTTP request methods.
 */
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
} as const;

/**
 * API endpoints - RESTful patterns (baseURL already includes /api).
 */
export const ENDPOINTS = {
  DEBUG: '/debug',
  HEALTH: '/health',
  PING: '/ping',
  INVITATIONS: '/invitations',
  LOGS: '/logs',
  ORGANIZATIONS: '/organizations',
  PROJECTS: '/projects',
  TEAMS: '/teams',
  USERS: '/users',
  JIRA: '/integrations/jira',
  REPOSITORIES: '/repositories',
  PRODUCTS: '/products',
} as const;

/**
 * Build RESTful endpoint for a project document.
 * @param projectId - The project ID
 * @param documentType - The document type (e.g., 'prd', 'technical-spec')
 * @returns The document endpoint path
 */
export function buildDocumentEndpoint(projectId: string, documentType: string): string {
  return `/projects/${projectId}/documents/${documentType}`;
}

/**
 * Build RESTful endpoint for a specific subtask.
 * @param projectId - The project ID
 * @param taskId - The task ID
 * @param subtaskId - The subtask ID
 * @returns The subtask endpoint path
 */
export function buildSubtaskEndpoint(projectId: string, taskId: string, subtaskId: string): string {
  return `/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}`;
}

/**
 * Build RESTful endpoint for task subtasks.
 * @param projectId - The project ID
 * @param taskId - The task ID
 * @returns The subtasks endpoint path
 */
export function buildSubtasksEndpoint(projectId: string, taskId: string): string {
  return `/projects/${projectId}/tasks/${taskId}/subtasks`;
}

/**
 * Build RESTful endpoint for a specific task.
 * @param projectId - The project ID
 * @param taskId - The task ID
 * @returns The task endpoint path
 */
export function buildTaskEndpoint(projectId: string, taskId: string): string {
  return `/projects/${projectId}/tasks/${taskId}`;
}

/**
 * Build RESTful endpoint for project tasks.
 * @param projectId - The project ID
 * @returns The tasks endpoint path
 */
export function buildTasksEndpoint(projectId: string): string {
  return `/projects/${projectId}/tasks`;
}
